import { useRef } from "react";
import { replacementFor } from "@/components/ui/use-keep-focus";

type AutoFocusHandlers = {
	onOpenAutoFocus?: (event: Event) => void;
	onCloseAutoFocus?: (event: Event) => void;
};

/**
 * Gives focus back, when a dialog closes, to whatever held it when the dialog
 * opened. Radix's modal content gives focus back only to the dialog's own
 * Trigger (https://github.com/radix-ui/primitives/blob/main/packages/react/dialog/src/dialog.tsx),
 * so a dialog opened from a menu entry or after a server's answer would leave
 * keyboard focus on the page. A caller's own handlers run first, and one that
 * prevents the default keeps the last word.
 *
 * When what the dialog did has taken that control away, focus goes to the one
 * that took its place, where a container around it keeps focus that way.
 */
export function useReturnFocus({
	onOpenAutoFocus,
	onCloseAutoFocus,
}: AutoFocusHandlers): Required<AutoFocusHandlers> {
	const returnTo = useRef<HTMLElement | null>(null);
	return {
		onOpenAutoFocus: (event) => {
			returnTo.current =
				document.activeElement instanceof HTMLElement
					? document.activeElement
					: null;
			onOpenAutoFocus?.(event);
		},
		onCloseAutoFocus: (event) => {
			onCloseAutoFocus?.(event);
			const opener = returnTo.current;
			returnTo.current = null;
			if (event.defaultPrevented || !opener || opener === document.body) {
				return;
			}
			const target = opener.isConnected ? opener : replacementFor(opener);
			if (target) {
				event.preventDefault();
				target.focus();
			}
		},
	};
}
